"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { useActionState } from "react";
import {
  type BetaTestFormState,
  submitBetaTestForm,
} from "@/app/actions/beta-test-actions";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { SelectField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const initialState: BetaTestFormState = {
  message: "",
  success: false,
};

export function BetaTestForm() {
  const t = useTranslations("betaTestForm");
  const locale = useLocale();
  const [state, formAction] = useActionState<BetaTestFormState, FormData>(
    submitBetaTestForm,
    initialState,
  );

  const roleOptions = [
    { value: "company", label: t("roles.company") },
    { value: "institution", label: t("roles.institution") },
    { value: "artist", label: t("roles.artist") },
    { value: "auction", label: t("roles.auction") },
    { value: "gallery", label: t("roles.gallery") },
    { value: "museum", label: t("roles.museum") },
    { value: "other", label: t("roles.other") },
  ];

  return (
    <Form<BetaTestFormState>
      action={formAction}
      state={state}
      className="max-w-lg mx-auto space-y-6"
      submitButton={{
        children: t("submitButton"),
        pendingText: t("submittingButton"),
        variant: "yellow",
        className: "text-sm md:text-base",
      }}
    >
      <input type="hidden" name="locale" value={locale} />

      <div className="space-y-2 flex gap-4">
        <Input
          type="text"
          name="name"
          placeholder={t("namePlaceholder")}
          required
          className="w-full text-white text-sm md:text-base h-12 border-0 border-b-2 border-gray-600 placeholder:text-sm md:placeholder:text-lg placeholder:text-gray-400 focus:border-white rounded-none"
        />

        <Input
          type="email"
          name="email"
          placeholder={t("emailPlaceholder")}
          required
          className="w-full text-white text-sm md:text-base h-12 border-0 border-b-2 border-gray-600 placeholder:text-sm md:placeholder:text-lg placeholder:text-gray-400 focus:border-white rounded-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectField
          className="border-b-2 border-gray-600 mb-0 !text-gray-400 text-sm md:text-lg "
          name="role"
          placeholder={t("rolePlaceholder")}
          options={roleOptions}
          required
        />

        <Input
          type="text"
          name="roleName"
          placeholder={t("organizationPlaceholder")}
          required
          className="w-full text-white text-sm md:text-base h-12 border-0 border-b-2 border-gray-600 placeholder:text-sm md:placeholder:text-lg placeholder:text-gray-400 focus:border-white rounded-none "
        />
      </div>

      <div className="flex items-start space-x-3">
        <Checkbox
          id="dataConsent"
          name="dataConsent"
          required
          className="mt-0.5 border-gray-600 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
        />
        <Label
          htmlFor="dataConsent"
          className="text-white text-sm leading-relaxed cursor-pointer inline text-left md:text-initial"
        >
          {t("consentText")}{" "}
          <Link
            href="/privacy-policy"
            className="text-yellow-500 hover:text-yellow-400 underline transition-colors inline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("privacyPolicyLink")}
          </Link>
        </Label>
      </div>
    </Form>
  );
}
